import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet, Navigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, FileText, Image, HelpCircle, Settings, 
  Calendar, BarChart3, LogOut, Menu, X, ChevronRight, Bell, Mail, Sparkles, Calculator, FolderOpen
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { cn, apiRequest } from '../../lib/utils';

const SIDEBAR_LINKS = [
  { href: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/admin/leads', label: 'Leads', icon: Users },
  { href: '/admin/lead-scoring', label: 'Lead Scoring', icon: Calculator },
  { href: '/admin/appointments', label: 'Appointments', icon: Calendar },
  { href: '/admin/appointment-settings', label: 'Scheduling Settings', icon: Settings },
  { href: '/admin/email-campaigns', label: 'Email Campaigns', icon: Mail },
  { href: '/admin/blogs', label: 'Blog Posts', icon: FileText },
  { href: '/admin/gallery', label: 'Gallery', icon: Image },
  { href: '/admin/faqs', label: 'FAQs', icon: HelpCircle },
  { href: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { href: '/admin/assets', label: 'Corporate Assets', icon: FolderOpen },
  { href: '/admin/features', label: 'Feature Showcase', icon: Sparkles },
];

const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_2837485d-5657-4508-82a6-ac79384a460f/artifacts/oj0ocik8_4.jpg';

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({ leads: 0, appointments: 0 });

  useEffect(() => {
    if (user) {
      apiRequest('/admin/analytics/summary')
        .then((data) => {
          setStats({
            leads: data.total_leads || 0,
            appointments: data.total_appointments || 0
          });
        })
        .catch(console.error);
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 h-full w-64 bg-slate-900 z-50 transition-transform lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={LOGO_DARK} 
                alt="Community Dental Lab"
                className="h-20 w-auto object-contain"
              />
            </Link>
            <p className="text-xs text-slate-400 mt-2">Admin Panel</p>
          </div>

          {/* Nav Links */}
          <nav className="flex-1 overflow-y-auto py-6">
            {SIDEBAR_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "admin-sidebar-link",
                  location.pathname === link.href && "active"
                )}
                onClick={() => setSidebarOpen(false)}
              >
                <link.icon className="w-5 h-5" />
                <span>{link.label}</span>
                {link.label === 'Leads' && stats.leads > 0 && (
                  <span className="ml-auto bg-orange-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {stats.leads}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {user.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user.username}</p>
                <p className="text-slate-400 text-xs truncate">{user.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="bg-white border-b sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-sidebar-toggle"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div className="hidden sm:flex items-center text-sm text-slate-500">
                <Link to="/" className="hover:text-orange-600">Home</Link>
                <ChevronRight className="w-4 h-4 mx-2" />
                <span className="text-slate-900 font-medium">Admin</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button className="relative p-2 text-slate-600 hover:text-slate-900">
                <Bell className="w-5 h-5" />
                {(stats.leads + stats.appointments) > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-orange-600 text-white text-xs rounded-full flex items-center justify-center">
                    {stats.leads + stats.appointments}
                  </span>
                )}
              </button>
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm">
                  View Site
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
